/**
 * PreviewRitual Component
 *
 * Displays a preview of the ritual before creation.
 * Shows all ritual details in a clean, organized layout.
 * Compatible with dark/light mode.
 */

import { Calendar, ChevronsDown, Link as LinkIcon, MapPin, MessageCircle } from "lucide-react"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible } from "@/components/ui/collapsible"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { getAssetUrl } from "@/utils/assets"
import { getRitualTypeLabel, RitualTypeEnum, type RitualTypeLabel } from "../types"
import { formatDuration } from "../utils/formatDuration"
import { CourseModulesList } from "./CourseModulesList"

// ============================================================================
// Types
// ============================================================================

export type RitualType = RitualTypeLabel

export interface SocialLink {
  platform: string
  url: string
}

export interface Organizer {
  id: string
  name: string
  avatar?: string
  role?: string
  socialLinks?: SocialLink[]
  followers?: number
  type?: "user" | "community"
  isCreator?: boolean
}

export interface PreviewRitualProps {
  // Basic Info
  title: string
  description: string

  // Course Modules (for Self Guided Course)
  courseModules?: Array<{
    id: string
    title: string
    description: string
    duration?: string
    order: number
    isFree: boolean
    videoUrl?: string
  }>

  // Details
  type: RitualType
  fee: string
  duration: string
  date: string
  time: string
  repeat: string
  location: string
  meetingLink?: string
  websiteLink?: string

  // Content
  whatYoullNeed: string[]
  whatYoullExperience: string[]
  intention: string
  intentions?: Array<{ id: string; name: string }>
  steps?: string[]

  // Organizers
  organizers: Organizer[]

  // Media
  coverImage: string
  images: string[]

  // Tags
  tags: string[]

  // Actions
  onBackToEdit: () => void
  onCreateRitual: () => void
  onSaveDraft?: () => void
  isSubmitting?: boolean
}

// ============================================================================
// Sub-Components
// ============================================================================

// Header Component
function PreviewHeader() {
  const { t } = useTranslation("rituals")

  const scrollToHero = () => {
    const heroElement = document.getElementById("preview-hero")
    if (heroElement) {
      heroElement.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <header className="text-center pb-5 md:pb-7.5 md:pt-11.5 relative">
      <h1 className="section-heading text-primary uppercase text-lg sm:text-[34px] leading-normal font-semibold">
        {t("preview.title")}
      </h1>
      <p className="text-primary max-w-2xl mx-auto mt-5 text-sm leading-6.25">
        {t("preview.description")}
      </p>

      {/* Down Arrow Button */}
      <button
        type="button"
        onClick={scrollToHero}
        className="mt-7.5 md:my-7.5  text-primary hover:text-brand-olive transition-colors animate-bounce"
        aria-label="Scroll to preview"
      >
        <ChevronsDown className="w-7.5 h-7.5" />
      </button>
    </header>
  )
}

// Hero Component (Simple Image Display - No Carousel for MVP)
function PreviewHero({ coverImage }: { coverImage: string }) {
  const placeholderImage = getAssetUrl("placeholder.png")

  return (
    <div className="relative overflow-hidden bg-muted md:rounded-t-2xl">
      <img
        src={coverImage || placeholderImage}
        alt="Ritual cover"
        className="aspect-[16/9] md:aspect-[9/4] w-full object-cover"
        onError={e => {
          const target = e.currentTarget
          target.src = placeholderImage
        }}
      />
    </div>
  )
}

// Title and Description Section
function TitleAndDescription({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col gap-2.5">
      <h2 className="section-heading text-primary uppercase text-lg leading-6.25 md:text-[28px] md:leading-11.25">
        {title}
      </h2>
      <p className="text-primary whitespace-pre-wrap text-sm leading-7.5">{description}</p>
    </div>
  )
}

// Details Sidebar (Right Column, Sticky)
function DetailsSidebar({
  fee,
  type,
  duration,
  date,
  time,
  repeat,
  location,
  meetingLink,
  websiteLink,
}: {
  fee: string
  type: RitualType
  duration: string
  date: string
  time: string
  repeat: string
  location: string
  meetingLink?: string
  websiteLink?: string
}) {
  const isPaid = fee.toLowerCase().includes("paid") || fee.toLowerCase().includes("$")

  return (
    <div className="bg-card rounded-[20px] p-5 space-y-5 border border-color-brand-white-alpha-10">
      {/* Fee Badge + Type */}
      <div className="flex items-center gap-2.5">
        <Badge
          className={cn(
            "text-white",
            isPaid ? "px-1.5 py-0.5 bg-color-brand-terracotta rounded-[5px]" : "bg-brand-olive",
          )}
        >
          {fee}
        </Badge>
        <span className="text-foreground font-medium text-[13px] leading-5">{type}</span>
      </div>

      <div className="flex flex-col gap-2.5">
        {/* Duration, Date, Time, Repeat */}
        <div className="flex items-center gap-2.5">
          <Calendar className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground">
              {formatDuration(duration)} - {date}, {time}
            </p>
            <p className="text-muted-foreground leading-6">{repeat}</p>
          </div>
        </div>

        {/* Location - Only for IN_PERSON */}
        {type === getRitualTypeLabel(RitualTypeEnum.IN_PERSON) && location && (
          <div className="flex items-center gap-2.5">
            <MapPin className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground leading-6">{location}</p>
          </div>
        )}

        {/* Meeting Link - Only for Virtual Course */}
        {type === getRitualTypeLabel(RitualTypeEnum.VIRTUAL_COURSE) && meetingLink && (
          <div className="flex items-center gap-2.5 text-brand-olive ">
            <LinkIcon className="w-6 h-6" />
            <button
              type="button"
              className="text-sm leading-6 text-brand-olive hover:underline text-left"
              onClick={() => window.open(meetingLink, "_blank", "noopener,noreferrer")}
            >
              Join Meeting
            </button>
          </div>
        )}

        {/* Website Link */}
        {websiteLink && (
          <button
            type="button"
            className="flex items-center gap-2.5 leading-6 text-sm text-brand-olive hover:underline"
            onClick={() => window.open(websiteLink, "_blank", "noopener,noreferrer")}
          >
            <LinkIcon className="w-6 h-6" />
            Visit website
          </button>
        )}
      </div>
    </div>
  )
}

// What You'll Need Section
function WhatYoullNeed({ items }: { items: string[] }) {
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <p key={index} className="text-muted-foreground text-sm leading-5.5">
          • {item}
        </p>
      ))}
    </div>
  )
}

// Box4: Organizers (with single reusable OrganizerCard)
function OrganizerCard({ organizer }: { organizer: Organizer }) {
  return (
    <div className="flex items-center justify-between">
      {/* Profile Image */}
      <div className="flex items-center gap-3.75">
        <div className="w-13.75 h-13.75 rounded-full bg-brand-olive flex-shrink-0 overflow-hidden">
          {organizer.avatar ? (
            <img
              src={organizer.avatar}
              alt={organizer.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Avatar fallback={organizer.name || "?"} size="md" className="w-full h-full" />
          )}
        </div>

        {/* Name and Followers */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{organizer.name}</p>
          {organizer.followers != null && (
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-xs text-muted-foreground">
                {organizer.followers} {organizer.type === "community" ? "members" : "followers"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Chat Icon Button - hide for creator */}
      {!organizer.isCreator && (
        <button
          type="button"
          className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-brand-olive hover:border-brand-olive transition-colors"
          aria-label={`Chat with ${organizer.name}`}
        >
          <MessageCircle className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

function OrganizersSection({ organizers }: { organizers: Organizer[] }) {
  const { t } = useTranslation("rituals")
  return (
    <div className="bg-card rounded-lg p-5 border border-color-brand-white-alpha-10">
      <div className="flex items-center gap-1.25 mb-3">
        <h3 className="text-[18px] font-semibold leading-7.5 text-foreground uppercase">
          {t("preview.organizers")}
        </h3>
        <span className="text-sm text-muted-foreground">({organizers.length})</span>
      </div>
      <div className="space-y-5">
        {organizers.map(organizer => (
          <OrganizerCard key={organizer.id} organizer={organizer} />
        ))}
      </div>
    </div>
  )
}

// What You'll Experience Section
function WhatYoullExperience({ items }: { items: string[] }) {
  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <p key={index} className="text-muted-foreground text-sm leading-5.5">
          • {item}
        </p>
      ))}
    </div>
  )
}

// Intention Section
function Intention({
  intention,
  intentions,
}: {
  intention?: string
  intentions?: Array<{ id: string; name: string }>
}) {
  if (intentions && intentions.length > 0) {
    return (
      <div className="flex flex-wrap gap-2">
        {intentions.map(i => (
          <span
            key={i.id}
            className="text-[13px] leading-[20px] border border-text-primary rounded-md px-2.5 py-2 text-text-primary whitespace-nowrap"
          >
            {i.name}
          </span>
        ))}
      </div>
    )
  }
  return (
    <p className="text-muted-foreground text-sm whitespace-pre-wrap leading-5.5">{intention}</p>
  )
}

// Steps Section (for Self-Guided rituals)
function StepsSection({ steps }: { steps: string[] }) {
  return (
    <div className="space-y-2">
      {steps.map((step, index) => (
        <p key={index} className="text-muted-foreground text-sm leading-5.5">
          {step}
        </p>
      ))}
    </div>
  )
}

// Tags Section
function TagsSection({ tags }: { tags: string[] }) {
  return (
    <div className=" rounded-lg">
      <div className="flex flex-wrap gap-2.5">
        {tags.map(tag => (
          <Badge
            key={tag}
            variant="secondary"
            className="gap-1 p-2.5 rounded-lg text-[13px] leading-5"
          >
            <span className="font-medium">{tag}</span>
          </Badge>
        ))}
      </div>
    </div>
  )
}

// Action Buttons (Sticky Footer)
function PreviewActions({
  onBackToEdit,
  onCreateRitual,
  onSaveDraft,
  isSubmitting,
}: {
  onBackToEdit: () => void
  onCreateRitual: () => void
  onSaveDraft?: () => void
  isSubmitting?: boolean
}) {
  const { t } = useTranslation("rituals")
  return (
    <footer className="sticky bottom-0 z-10 px-5 border-t bg-background/95 backdrop-blur supports-backdrop-filter:bg-[#1C1C1CE5]">
      <div className="hidden md:flex flex-col sm:flex-row gap-4 justify-center py-4">
        <Button
          onClick={onBackToEdit}
          variant="outline"
          className="px-20 py-3"
          disabled={isSubmitting}
        >
          {t("buttons.edit")}
        </Button>

        {onSaveDraft && (
          <Button
            onClick={onSaveDraft}
            variant="outline"
            className="px-20 py-3"
            disabled={isSubmitting}
          >
            {t("preview.saveDraft")}
          </Button>
        )}

        <Button
          onClick={onCreateRitual}
          className="px-20 py-3 bg-brand-terracotta hover:bg-brand-terracotta-bright text-white font-semibold"
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting ? t("preview.creatingRitual") : t("preview.createRitual")}
        </Button>
      </div>
      <div className="flex md:hidden flex-col md:flex-row gap-4 justify-center py-4">
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2.5">
          <Button
            onClick={onBackToEdit}
            variant="outline"
            className="px-20 py-3"
            disabled={isSubmitting}
          >
            {t("buttons.edit")}
          </Button>

          {onSaveDraft && (
            <Button
              onClick={onSaveDraft}
              variant="outline"
              className="px-20 py-3"
              disabled={isSubmitting}
            >
              {t("preview.saveDraft")}
            </Button>
          )}
        </div>

        <Button
          onClick={onCreateRitual}
          className="px-20 py-3 bg-brand-terracotta hover:bg-brand-terracotta-bright text-white font-semibold"
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting ? t("preview.creatingRitual") : t("preview.createRitual")}
        </Button>
      </div>
    </footer>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function PreviewRitual({
  title,
  description,
  type,
  fee,
  duration,
  date,
  time,
  repeat,
  location,
  meetingLink,
  websiteLink,
  whatYoullNeed,
  whatYoullExperience,
  intention,
  intentions,
  steps,
  organizers,
  coverImage,
  tags,
  courseModules,
  onBackToEdit,
  onCreateRitual,
  onSaveDraft,
  isSubmitting,
}: PreviewRitualProps) {
  const { t } = useTranslation("rituals")

  // Fallback empty arrays for optional array props
  const needItems = whatYoullNeed || []
  const experienceItems = whatYoullExperience || []
  const organizerList = organizers || []
  const tagList = tags || []
  const stepsList = steps || []

  // Note: format once in DetailsSidebar via formatDuration(duration)

  // Check if steps should be shown (Self-Guided type with steps)
  const shouldShowSteps =
    (type === getRitualTypeLabel(RitualTypeEnum.SELF_GUIDED) ||
      type === getRitualTypeLabel(RitualTypeEnum.SELF_GUIDED_COURSE)) &&
    stepsList.length > 0

  return (
    <div className="min-h-screen bg-background">
      {/* Normal Header */}
      <PreviewHeader />

      <div className="border-0 md:border border-black-500 bg:border-card rounded-2xl md:mx-17.5 mb-5">
        {/* Hero */}
        <div id="preview-hero">
          <PreviewHero coverImage={coverImage} />
        </div>

        {/* Main Content - 2 Columns (70/30) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-7.5 py-7.5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-7.5">
            {/* Left Column (70%) */}
            <div className="lg:col-span-8 space-y-7.5">
              <TitleAndDescription title={title} description={description} />
              <div className="space-y-5">
                {shouldShowSteps && (
                  <Collapsible title={t("preview.steps")}>
                    <StepsSection steps={stepsList} />
                  </Collapsible>
                )}
                {needItems.length > 0 && (
                  <Collapsible title={t("preview.whatYoullNeed")}>
                    <WhatYoullNeed items={needItems} />
                  </Collapsible>
                )}
                {experienceItems.length > 0 && (
                  <Collapsible title={t("preview.whatYoullExperience")}>
                    <WhatYoullExperience items={experienceItems} />
                  </Collapsible>
                )}
                {type === getRitualTypeLabel(RitualTypeEnum.SELF_GUIDED_COURSE) &&
                  courseModules &&
                  courseModules.length > 0 && (
                    <Collapsible title={t("preview.courseModules")}>
                      <CourseModulesList modules={courseModules} hideWrapper />
                    </Collapsible>
                  )}
                {(intention || (intentions && intentions.length > 0)) && (
                  <Collapsible title={t("preview.intention")}>
                    <Intention intention={intention} intentions={intentions} />
                  </Collapsible>
                )}
                {tagList.length > 0 && <TagsSection tags={tagList} />}
              </div>
            </div>

            {/* Right Column (30%) */}
            <div className="lg:col-span-4 space-y-6">
              <div className="space-y-5">
                <DetailsSidebar
                  fee={fee}
                  type={type}
                  duration={duration}
                  date={date}
                  time={time}
                  repeat={repeat}
                  location={location}
                  meetingLink={meetingLink}
                  websiteLink={websiteLink}
                />
                {organizerList.length > 0 && <OrganizersSection organizers={organizerList} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <PreviewActions
        onBackToEdit={onBackToEdit}
        onCreateRitual={onCreateRitual}
        onSaveDraft={onSaveDraft}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
